import { Logo } from "@/components/Logo";
import { QrCode, Copy, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LevelSeal, LevelSealLabel } from "@/components/LevelSeal";
import { copyToClipboard } from "@/lib/clipboard";

export function WalletCard({ user, qrImage, onShowQr, seal = null, progress = null }) {
  if (!user) return null;
  return (
    <div
      data-testid="wallet-card"
      className="relative overflow-hidden rounded-2xl text-white p-5 shadow-lg ring-1 ring-[hsl(var(--gold)/0.45)] bg-wallet-card"
      style={{ aspectRatio: "1.586/1", maxHeight: 340 }}
    >
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), radial-gradient(rgba(255,255,255,0.2) 1px, transparent 1px)",
          backgroundPosition: "0 0, 4px 4px",
          backgroundSize: "8px 8px",
        }}
      />

      <div className="relative flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-white/10 rounded-lg p-1.5 ring-1 ring-white/15">
            <Logo size={26} showText={false} variant="white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] uppercase tracking-[0.18em] text-white/80">Carteira Digital</span>
            <span className="font-display font-semibold text-sm">CEAP BRASIL</span>
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid="wallet-level">
          {seal ? <LevelSeal seal={seal} size={30} /> : null}
          <LevelSealLabel seal={seal} level={user.level || "Aluno CEAP"} />
        </div>
      </div>

      <div className="relative mt-5 grid grid-cols-[1fr_auto] gap-4 items-end">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-white/60">Aluno</p>
          <h3 className="font-display font-semibold text-lg leading-tight truncate" data-testid="wallet-name">
            {user.full_name}
          </h3>
          <p className="text-[12px] text-white/70 truncate">
            {user.cargo || "—"}
            {user.cargo && user.orgao ? " • " : ""}
            {user.orgao || ""}
          </p>
          <p className="text-[11px] text-white/60 truncate">
            {[user.cidade, user.estado].filter(Boolean).join(" / ")}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <span className="font-mono-id text-[12.5px] text-white/85" data-testid="wallet-ceap-id">
              {user.ceap_id}
            </span>
            <button
              type="button"
              className="text-white/60 hover:text-white"
              onClick={async () => {
                const ok = await copyToClipboard(user.ceap_id || "");
                if (ok) {
                  toast.success("CEAP ID copiado");
                } else {
                  toast.error("Não foi possível copiar. Anote: " + (user.ceap_id || ""));
                }
              }}
              aria-label="Copiar CEAP ID"
              data-testid="wallet-copy-id"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onShowQr}
          className="shrink-0 rounded-xl bg-white p-2 ring-1 ring-[hsl(var(--gold)/0.6)] hover:scale-[1.02] active:scale-[0.99] transition-transform"
          aria-label="Mostrar QR"
          data-testid="wallet-qr-open-button"
        >
          {qrImage ? (
            <img src={qrImage} alt="QR" className="w-[88px] h-[88px] object-contain" />
          ) : (
            <div className="w-[88px] h-[88px] flex items-center justify-center text-[hsl(var(--primary))]">
              <QrCode className="w-12 h-12" />
            </div>
          )}
        </button>
      </div>

      {progress && progress.next_level && (
        <div className="relative mt-3" data-testid="wallet-progress">
          <div className="flex items-center justify-between text-[10.5px] tracking-wider uppercase">
            <span className="text-white/70">{progress.percent}% rumo a {progress.next_level}</span>
            <span className="text-white/70">faltam {progress.hours_remaining}h</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${progress.percent}%`,
                background: "linear-gradient(90deg, #c9a961 0%, #f1d68b 100%)",
              }}
            />
          </div>
        </div>
      )}

      <div className="absolute bottom-2 right-3 text-[9.5px] tracking-wider text-white/40">
        DOCUMENTO DIGITAL OFICIAL CEAP BRASIL
      </div>
    </div>
  );
}

export function WalletStats({ user }) {
  const Stat = ({ value, label }) => (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-white p-3">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-display font-semibold text-2xl text-[hsl(var(--primary))]" data-testid={`wallet-stat-${label.replace(/\s/g, '-').toLowerCase()}`}>{value}</p>
    </div>
  );
  return (
    <div className="grid grid-cols-3 gap-2">
      <Stat value={user.courses_completed || 0} label="Cursos" />
      <Stat value={`${user.hours_accumulated || 0}h`} label="Horas" />
      <Stat value={user.is_validated ? "Sim" : "—"} label="Validado" />
    </div>
  );
}

export function ShareWalletButton({ user }) {
  const onShare = async () => {
    const text = `Sou ${user.full_name}, ${user.cargo || ""} - ${user.orgao || ""}. Aluno CEAP BRASIL: ${user.ceap_id}.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Carteira CEAP", text });
        return;
      } catch {
        /* usuário cancelou ou share falhou — cai pra cópia */
      }
    }
    const ok = await copyToClipboard(text);
    if (ok) {
      toast.success("Dados da carteira copiados");
    } else {
      toast.error("Não foi possível compartilhar agora.");
    }
  };
  return (
    <Button variant="outline" onClick={onShare} data-testid="wallet-share-button">
      Compartilhar
    </Button>
  );
}
